import apiConfig from '../config/apiConfig';
import type { GoogleDisconnectResponse } from './gmailService';

const isDev = process.env.NODE_ENV !== 'production';
const devLog = (...args: unknown[]): void => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

export interface YouTubeChannel {
  id: string;
  title: string;
  description: string;
  handle: string;
  custom_url: string;
  thumbnail_url: string;
  statistics: {
    view_count: string;
    subscriber_count: string;
    video_count: string;
  };
  country?: string;
  published_at: string;
}

export interface ChannelSearchResponse {
  query: string;
  total_results: number;
  channels: YouTubeChannel[];
}

export interface MonetizationOpportunity {
  type: string;
  value: string;
  service: string;
  context: string;
  confidence: string;
  verified_brand: boolean;
  source: string;
  benefit: string;
  valid_until: string | null;
  video_url?: string;
  video_title?: string;
  video_id?: string;
}

export interface DeduplicatedOpportunity extends MonetizationOpportunity {
  appearance_count: number;
  videos: {
    video_id: string;
    title: string;
    url: string;
    publishedAt?: string;
    thumbnailUrl?: string;
  }[];
  // Backend-enriched fields (Sprint 9). Optional because older payloads
  // may pre-date the brand-color/category enrichment pipeline.
  icon_url?: string;
  brand_color?: string;
  category?: string;
}

export interface VideoMetadata {
  title: string;
  video_id: string;
  published_at: string;
  description: string;
  url: string;
  thumbnail_url?: string;
}

export interface AnalyzedVideo {
  video_metadata: VideoMetadata;
  monetization_opportunities: MonetizationOpportunity[];
  analysis_metadata: {
    tools_used: { tool_name: string; result: string; success: boolean }[];
    analyzed_at: string;
  };
}

export interface CreatorScraperResponse {
  creator: string;
  handle: string;
  thumbnail_url?: string;
  total_videos_analyzed: number;
  date_range: {
    start: string;
    end: string;
  };
  videos: AnalyzedVideo[];
  deduplicated_opportunities?: DeduplicatedOpportunity[];
  summary: {
    creator: string;
    total_videos_analyzed: number;
    videos: {
      title: string;
      video_id: string;
      monetization_count: number;
      url: string;
    }[];
    analysis_summary: {
      total_opportunities: number;
      by_type: {
        [key: string]: number;
      };
    };
  };
}

export interface DateHelperResponse {
  current_date: string;
  example_ranges: {
    last_month: { start_date: string; end_date: string };
    last_3_months: { start_date: string; end_date: string };
    last_year: { start_date: string; end_date: string };
    january_this_year: { start_date: string; end_date: string };
  };
  format_info: {
    date_format: string;
    example: string;
  };
  notes: string[];
}

export interface ScrapeProgressResponse {
  status: string;
  progress: {
    total_videos_found: number;
    videos_processed: number;
    opportunities_found: number;
    current_video?: string;
    /** ID of the YouTube video being analyzed right now. Backend
     *  populates this on each per-video progress update; the frontend
     *  uses it to render a "currently analyzing" thumbnail in the
     *  multi-row progress card. */
    current_video_id?: string;
    /** Pre-built `https://i.ytimg.com/vi/<id>/hqdefault.jpg` URL.
     *  Backend constructs it so the UI doesn't have to. */
    current_video_thumbnail?: string;
    percentage_complete: number;
    status_message: string;
  };
  is_complete: boolean;
  job_id?: string;
  result?: CreatorScraperResponse;
  error?: string;
}

const youtubeService = {
  /**
   * Search for YouTube channels by query
   */
  // authToken: Clerk JWT (required by backend `/api/youtube-channel-search`,
  // which gates the call behind `Depends(get_current_user)`). Older callers
  // were silently 401'ing — surfaces as "Failed to fetch channels" in the UI.
  searchYouTubeChannels: async (
    query: string,
    maxResults: number = 5,
    authToken?: string,
  ): Promise<ChannelSearchResponse> => {
    try {
      const url = `${apiConfig.baseUrl}${apiConfig.endpoints.youtubeChannelSearch}`;
      devLog('Searching YouTube channels at:', url, 'with query:', query);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, max_results: maxResults }),
      });

      if (!response.ok) {
        console.error('YouTube channel search failed:', response.status, response.statusText);
        throw new Error(`Error searching for YouTube channels: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to search YouTube channels:', error);
      throw error;
    }
  },

  // NOTE: the synchronous analyzeYouTubeCreator (POST /api/youtube-creator-
  // scraper) was removed in the scraping-consolidation cleanup — it had no UI
  // callers and its backend endpoint (which bypassed the per-scrape caps) was
  // deleted. Use startYouTubeAnalysis below, which funnels through the
  // canonical capped pipeline.

  /**
   * Get date helper information for the scraper
   */
  getDateHelper: async (): Promise<DateHelperResponse> => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.youtubeDateHelper}`);
      
      if (!response.ok) {
        throw new Error(`Error fetching date helper data: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get date helper information:', error);
      throw error;
    }
  },

  /**
   * Check YouTube API health
   */
  checkYouTubeHealth: async (): Promise<{status: string}> => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.youtubeHealth}`);
      
      if (!response.ok) {
        throw new Error(`YouTube API health check failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('YouTube API health check failed:', error);
      throw error;
    }
  },

  /**
   * Start an asynchronous YouTube creator analysis job.
   * authToken: Clerk JWT (required by backend `Depends(get_current_user)`).
   */
  startYouTubeAnalysis: async (
    handle: string,
    creatorName: string,
    startDate: string,
    endDate: string,
    maxVideos: number = 50,
    maxWorkers: number = 3,
    delayBetweenRequests: number = 0.5,
    authToken?: string,
  ): Promise<{ job_id: string }> => {
    try {
      devLog(`Starting analysis job for ${creatorName} (@${handle}) with ${maxVideos} videos from ${startDate} to ${endDate}`);
      console.log(`Speed settings: ${maxWorkers} workers, ${delayBetweenRequests}s delay`);

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const response = await fetch(`${apiConfig.baseUrl}/api/youtube-creator-scraper-async`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          handle,
          creator_name: creatorName,
          start_date: startDate,
          end_date: endDate,
          max_videos: maxVideos,
          max_workers: maxWorkers,
          delay_between_requests: delayBetweenRequests,
        }),
      });

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || response.statusText;
        } catch {
          errorMessage = response.statusText;
        }
        
        console.error(`API Error (${response.status}): ${errorMessage}`);
        throw new Error(`Error starting YouTube analysis: ${errorMessage}`);
      }

      const data = await response.json();
      console.log(`Successfully started analysis job: ${data.job_id}`);
      return data;
    } catch (error) {
      console.error('Failed to start YouTube analysis:', error);
      throw error;
    }
  },

  /**
   * Check the progress of an async YouTube creator analysis job
   */
  checkAnalysisProgress: async (jobId: string): Promise<ScrapeProgressResponse> => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}/api/youtube-scraper-progress/${jobId}`);
      
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || response.statusText;
        } catch {
          errorMessage = response.statusText;
        }
        
        console.error(`API Error (${response.status}): ${errorMessage}`);
        
        // Handle specific error cases
        if (response.status === 404) {
          throw new Error('Analysis job not found. It may have expired or been cancelled.');
        }
        
        throw new Error(`Error checking analysis progress: ${errorMessage}`);
      }

      const data = await response.json();
      
      // Log progress for debugging
      console.log(`Job ${jobId} progress: ${data.progress?.percentage_complete || 0}% - ${data.status}`);
      
      return data;
    } catch (error) {
      console.error('Failed to check analysis progress:', error);
      throw error;
    }
  },

  /**
   * Get cache statistics showing performance benefits
   */
  getCacheStats: async (): Promise<any> => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}/api/youtube-cache-stats`);
      
      if (!response.ok) {
        throw new Error(`Error fetching cache stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      throw error;
    }
  },
};

// Function to get YouTube OAuth URL from backend.
// Requires Clerk JWT — the endpoint authenticates via `get_current_user`
// so it can CSRF-bind the OAuth state token to the caller's user_id.
// Prior to this version we sent no Authorization header, which silently
// 401'd and prevented any user from starting (or retrying) a YouTube
// connection. Mirrors the Gmail OAuth URL pattern (gmailService.ts).
export const getYouTubeOAuthUrl = async (
  redirectUri: string,
  authToken: string,
): Promise<string> => {
  try {
    console.log('Fetching YouTube OAuth URL...');
    const response = await fetch(
      `${apiConfig.baseUrl}/api/user/youtube-oauth-url?redirect_uri=${encodeURIComponent(redirectUri)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get OAuth URL: ${response.status}`);
    }

    const data = await response.json();
    console.log('OAuth URL received:', data.oauth_url ? 'Success' : 'Missing URL');
    return data.oauth_url || data.url || '';
  } catch (error) {
    console.error('Error fetching OAuth URL:', error);
    throw error;
  }
};

/**
 * Deletes Bearri's stored YouTube access for this user (Studio numbers stop, cached ones are
 * forgotten, a click-through report job is removed best-effort). Same response as Gmail's
 * disconnect. Throws on any non-2xx so the caller keeps its local state as it was.
 */
export const disconnectYouTube = async (authToken: string): Promise<GoogleDisconnectResponse> => {
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-disconnect`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Couldn’t disconnect. Try again.');
  const data = await response.json();
  return { disconnected: data?.disconnected === true, google_access_removed: data?.google_access_removed === true };
};

// The user's YouTube subscriptions, read with the Google access the server stored when YouTube was connected.
// authToken: Clerk session token (REQUIRED, sent as Authorization: Bearer). No Google token ever goes from the browser:
// the server only uses what its own code exchange stored.
export const getUserYouTubeSubscriptions = async (authToken?: string): Promise<any[]> => {
  try {
    console.log('Fetching user YouTube subscriptions...');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Clerk JWT always goes in Authorization; backend verifies the signature.
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-subscriptions`, {
      method: 'GET',
      headers,
      credentials: 'include', // Include cookies for session/user identification
    });

    if (!response.ok) {
      // Check if it's a token expiration error
      const errorType = response.headers.get('X-Error-Type');
      
      // Clear connection status if token is invalid/expired
      if (response.status === 401 || errorType === 'token_expired' || errorType === 'token_invalid') {
        localStorage.removeItem('youtube_connected');
        localStorage.removeItem('youtube_subscriptions_count');
        localStorage.removeItem('bearri_my_youtube_handle');
        localStorage.removeItem('bearri_my_youtube_title');
        
        if (errorType === 'token_expired' || errorType === 'token_invalid') {
          throw new Error('YouTube connection expired. Please reconnect your YouTube account from the Profile page.');
        } else {
          throw new Error('Authentication required. Please sign in with Google again.');
        }
      } else if (response.status === 403) {
        const errorType = response.headers.get('X-Error-Type');
        localStorage.removeItem('youtube_connected');
        localStorage.removeItem('youtube_subscriptions_count');
        localStorage.removeItem('bearri_my_youtube_handle');
        localStorage.removeItem('bearri_my_youtube_title');
        if (errorType === 'token_expired_or_disconnected') {
          throw new Error('Your YouTube token has expired or was disconnected. Please reconnect your YouTube account to continue.');
        } else {
          throw new Error('YouTube access not granted. Please connect your YouTube account.');
        }
      } else {
        throw new Error(`Failed to fetch subscriptions: ${response.status}`);
      }
    }

    const data = await response.json();
    console.log('User subscriptions fetched:', data.subscriptions?.length || 0);
    if (data.my_channel) {
      if (data.my_channel.handle) {
        localStorage.setItem('bearri_my_youtube_handle', String(data.my_channel.handle).replace(/^@/, ''));
      }
      if (data.my_channel.title) {
        localStorage.setItem('bearri_my_youtube_title', String(data.my_channel.title));
      }
    }
    return data.subscriptions || [];
  } catch (error) {
    console.error('Error fetching user subscriptions:', error);
    throw error;
  }
};

// Shape of the user's own connected channel returned by
// /api/user/youtube-subscriptions. `subscriber_count` may be null for
// channels with the "hide subscriber count" privacy setting enabled.
export interface MyYouTubeChannel {
  channel_id: string;
  title: string;
  handle: string | null;
  subscriber_count: number | null;
  view_count: number | null;
  video_count: number | null;
  thumbnail_url: string | null;
}

// Same fetch as getUserYouTubeSubscriptions, but returns BOTH the
// subscriptions array AND the user's own channel object. Added so
// callers (e.g. OAuthCallback) can persist subscriber_count locally
// without breaking the many existing call sites of the original
// function.
export const getUserYouTubeSubscriptionsWithChannel = async (
  authToken: string,
): Promise<{ subscriptions: any[]; myChannel: MyYouTubeChannel | null }> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-subscriptions`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorType = response.headers.get('X-Error-Type');
    if (response.status === 401 || errorType === 'token_expired' || errorType === 'token_invalid') {
      localStorage.removeItem('youtube_connected');
      localStorage.removeItem('youtube_subscriptions_count');
      localStorage.removeItem('bearri_my_youtube_handle');
      localStorage.removeItem('bearri_my_youtube_title');
      if (errorType === 'token_expired' || errorType === 'token_invalid') {
        throw new Error('YouTube connection expired. Please reconnect your YouTube account from the Profile page.');
      }
      throw new Error('Authentication required. Please sign in with Google again.');
    }
    if (response.status === 403) {
      localStorage.removeItem('youtube_connected');
      localStorage.removeItem('youtube_subscriptions_count');
      localStorage.removeItem('bearri_my_youtube_handle');
      localStorage.removeItem('bearri_my_youtube_title');
      if (errorType === 'token_expired_or_disconnected') {
        throw new Error('Your YouTube token has expired or was disconnected. Please reconnect your YouTube account to continue.');
      }
      throw new Error('YouTube access not granted. Please connect your YouTube account.');
    }
    throw new Error(`Failed to fetch subscriptions: ${response.status}`);
  }

  const data = await response.json();
  const myChannel: MyYouTubeChannel | null = data.my_channel
    ? {
        channel_id: data.my_channel.channel_id,
        title: data.my_channel.title,
        handle: data.my_channel.handle ?? null,
        subscriber_count:
          data.my_channel.subscriber_count !== undefined
            ? data.my_channel.subscriber_count
            : null,
        view_count:
          data.my_channel.view_count !== undefined ? data.my_channel.view_count : null,
        video_count:
          data.my_channel.video_count !== undefined ? data.my_channel.video_count : null,
        thumbnail_url: data.my_channel.thumbnail_url ?? null,
      }
    : null;

  if (myChannel?.handle) {
    localStorage.setItem('bearri_my_youtube_handle', String(myChannel.handle).replace(/^@/, ''));
  }
  if (myChannel?.title) {
    localStorage.setItem('bearri_my_youtube_title', String(myChannel.title));
  }

  return {
    subscriptions: data.subscriptions ?? [],
    myChannel,
  };
};

// NOTE: bulkScrapeCreators (POST /api/youtube-bulk-scraper) was removed in
// the scraping-consolidation cleanup — no UI callers; the live bulk flow is
// dataService.startBulkScrape (POST /api/data/scrape-bulk).

export interface MyYoutubeUploadItem {
  video_id: string;
  title: string;
  published_at: string | null;
  thumbnail_url?: string | null;
  url: string;
  scraped: boolean;
}

export interface MyYoutubeUploadsResponse {
  success: boolean;
  platform: string;
  my_channel: {
    channel_id?: string;
    title?: string | null;
    handle?: string | null;
    thumbnail_url?: string | null;
  } | null;
  max_scrape_lookback_days: number;
  window_start: string;
  window_end: string;
  videos: MyYoutubeUploadItem[];
  summary: { listed: number; scraped: number; not_scraped: number };
}

/** List current user's YouTube uploads (Clerk JWT) with scraped flags; window is last ~4 months by default. */
export const getMyYoutubeUploads = async (
  authToken: string,
  maxVideos: number = 50,
  lookbackDays?: number,
): Promise<MyYoutubeUploadsResponse> => {
  const params = new URLSearchParams();
  if (maxVideos) params.set('max_videos', String(maxVideos));
  if (lookbackDays) params.set('lookback_days', String(lookbackDays));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-my-uploads${qs}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const errType = response.headers.get('X-Error-Type');
    let msg = response.statusText;
    try {
      const body = await response.json();
      msg = body.detail || body.message || msg;
    } catch {
      /* ignore */
    }
    const e = new Error(msg) as Error & { status?: number; errorType?: string | null };
    e.status = response.status;
    e.errorType = errType;
    throw e;
  }
  return response.json();
};

/** Start async scrape for the signed-in user's own channel (dates clamped server-side to ~4 months). */
export const startMyChannelScrape = async (
  authToken: string,
  opts?: { max_videos?: number; start_date?: string; end_date?: string; force_reanalyze?: boolean },
): Promise<{ job_id: string; handle: string; creator_name: string; message: string }> => {
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-scrape-my-channel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    credentials: 'include',
    body: JSON.stringify({
      max_videos: opts?.max_videos ?? 40,
      start_date: opts?.start_date ?? null,
      end_date: opts?.end_date ?? null,
      force_reanalyze: opts?.force_reanalyze ?? false,
    }),
  });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      const body = await response.json();
      msg = body.detail || body.message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return response.json();
};

/** Manually add a sponsor to the signed-in creator's own dashboard (ownership
 * verified server-side via their connected YouTube channel). */
export const addMySponsor = async (
  authToken: string,
  payload: {
    brand: string;
    code?: string | null;
    offer?: string | null;
    website?: string | null;
    brand_logo_url?: string | null;
    brand_color?: string | null;
    category?: string | null;
    videos?: { video_id: string; url?: string | null; title?: string | null; thumbnail_url?: string | null }[];
  },
): Promise<{ success: boolean; sponsorship_id: string; brand_id: string; handle: string }> => {
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-add-sponsorship`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      const body = await response.json();
      msg = body.detail || body.message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return response.json();
};

export default youtubeService; 